import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { ibanApi } from '../../services/rest/apiInstances';
import { IBANAccount } from '@/api/general';
import { get } from 'lodash';
import { onboardingApi } from '../../services/rest/apiInstances';
import { Currency } from '@/api/client';
import { buildClientApiConfig } from '@/helpers/apiRequestConfig';

export const fetchIbansList = createAsyncThunk('ibans/fetchIbansList', async () => {
    const options = buildClientApiConfig();
    try {
        const data = await ibanApi.listIBANAccounts(options);
        return data;
    } catch (error: any) {
        return error.data;
    }
});

export const fetchIbansSetup = createAsyncThunk(
    'ibans/fetchIbansSetup',
    async (clientId: string) => {
        try {
            const response = await onboardingApi.getIbansSetup({ clientId });
            return response.data;
        } catch (error: any) {
            throw error;
        }
    },
);

export interface IIbansState {
    isPending: boolean;
    errors: any;
    ibanList: IBANAccount[];
    settlementIbanList: any[];
    isOpenIbanDetailsModal: boolean;
    isOpenIbanClientDetailsModal: boolean;
    ibanToDisplay: IBANAccount | undefined;
    isOpenCreateIbanClientModal: boolean;
    clientId: string;
    refreshTrigger: number;
    unconfiguredCurrencies: Currency[] | undefined;
}

const initialState: IIbansState = {
    isPending: false,
    errors: null,
    settlementIbanList: [],
    ibanList: [],
    isOpenIbanDetailsModal: false,
    isOpenIbanClientDetailsModal: false,
    ibanToDisplay: undefined,
    isOpenCreateIbanClientModal: false,
    clientId: '',
    refreshTrigger: 0,
    unconfiguredCurrencies: undefined,
};

export const ibansSlice = createSlice({
    name: 'ibans',
    initialState,
    reducers: {
        openIbanDetailsModal: (state, action: PayloadAction<any>) => {
            state.isOpenIbanDetailsModal = true;
            state.ibanToDisplay = action.payload;
        },
        closeIbanDetailsModal: (state) => {
            state.isOpenIbanDetailsModal = false;
            state.ibanToDisplay = undefined;
        },
        openIbanClientDetailsModal: (state, action: PayloadAction<any>) => {
            state.isOpenIbanClientDetailsModal = true;
            state.ibanToDisplay = action.payload;
        },
        closeIbanClientDetailsModal: (state) => {
            state.isOpenIbanClientDetailsModal = false;
            state.ibanToDisplay = undefined;
        },
        openCreateIbanClientModal: (state, action: PayloadAction<any>) => {
            state.isOpenCreateIbanClientModal = true;
            state.clientId = action.payload;
        },
        closeCreateIbanClientModal: (state) => {
            state.isOpenCreateIbanClientModal = false;
            state.clientId = '';
        },
        triggerIbansRefresh: (state) => {
            state.refreshTrigger = Date.now();
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchIbansList.pending, (state) => {
                state.isPending = true;
            })
            .addCase(fetchIbansList.rejected, (state, action) => {
                state.isPending = false;
                state.errors = action.error;
            })
            .addCase(fetchIbansList.fulfilled, (state, action) => {
                const ibans = action.payload;
                const accountsList: IBANAccount[] = get(action, 'payload.data.accounts', []);

                let prefundIbans = accountsList.filter(
                    (iban: IBANAccount) => iban.type === 'prefund',
                );

                prefundIbans = prefundIbans.map((account: IBANAccount) => {
                    return {
                        ...account,
                        settlementBalance: accountsList.find(
                            (iban: any) =>
                                iban.type === 'settlement' && iban.currency === account.currency,
                        )?.balance,
                    };
                });

                state.isPending = false;
                state.ibanList = ibans;
                state.settlementIbanList = prefundIbans;
            })
            .addCase(fetchIbansSetup.pending, (state) => {
                state.isPending = true;
            })
            .addCase(fetchIbansSetup.rejected, (state, action) => {
                state.isPending = false;
                state.errors = action.error;
            })
            .addCase(fetchIbansSetup.fulfilled, (state, action) => {
                state.isPending = false;
                state.unconfiguredCurrencies = action.payload.unconfiguredCurrencies;
            });
    },
});

export const {
    openIbanDetailsModal,
    closeIbanDetailsModal,
    openIbanClientDetailsModal,
    closeIbanClientDetailsModal,
    openCreateIbanClientModal,
    closeCreateIbanClientModal,
    triggerIbansRefresh,
} = ibansSlice.actions;

export default ibansSlice.reducer;
